import * as _ from 'lodash';
import { Component, FormEvent } from 'react';
import { IWatrRequest, IWatrReimbursement, IListItem } from '@nkr/models/index';
import { Alert, Button, Col, ControlLabel, FormControl, FormGroup, Modal, Row } from 'react-bootstrap';

interface IValidation {
    initials: boolean,
    doctorFullName: boolean
}

interface IProps {
    show: boolean,
    data: IWatrRequest,
    types: IListItem[],
    reimbursements: IWatrReimbursement[],
    onRequestAdditionalDays: (requestId: number, additionalDays: number, additionalDaysSignOff: string, drFullNameSignOff: string) => void,
    onCancel: () => void
}

interface IState {
    initials: string,
    doctorFullName: string,
    additionalDays: number,
    showMedicalReasonConfirmation: boolean,
    showDoctorConfirmation: boolean,
    optionsList: number[],
    validation: IValidation,
    dirty: boolean
}

class RequestAdditional extends Component<IProps, IState> {
    readonly state: IState = {
        initials: '',
        doctorFullName: '',
        additionalDays: 0,
        showMedicalReasonConfirmation: false,
        showDoctorConfirmation: false,
        optionsList: [],
        validation: {
            initials: true,
            doctorFullName: true
        },
        dirty: false
    }

    getDaysReimbursed = (data: IWatrRequest) => {
        let selectedDays = 0;
        let optionsList = [];

        for (let i = 10; i > 0; i--) {
            if ((data.totalWagesReimbursedDays + i) > 30) {
                continue;
            }

            optionsList.push(i);

            if (data.additionalDaysRequested == i) {
                selectedDays = i;
            }
        }

        if (selectedDays == 0) {
            selectedDays = optionsList[0];
        }

        let additionalDays = this.state.additionalDays;

        if (selectedDays > 0 && selectedDays != this.state.additionalDays) {
            additionalDays = selectedDays;
        }

        return { optionsList, additionalDays };
    }

    componentWillReceiveProps = (nextProps: Readonly<IProps>) => {
        if (nextProps.data != null) {
            let totalDaysRequested = nextProps.data.totalWagesReimbursedDays + nextProps.data.additionalDaysRequested;

            let { optionsList, additionalDays } = this.getDaysReimbursed(nextProps.data);

            let showMedicalReasonConfirmation = true;
            let showDoctorConfirmation = false;

            totalDaysRequested += additionalDays;

            if (totalDaysRequested > 20) {
                showMedicalReasonConfirmation = false;
                showDoctorConfirmation = true;
            }

            this.setState({
                initials: '',
                doctorFullName: '',
                additionalDays,
                showMedicalReasonConfirmation,
                showDoctorConfirmation,
                optionsList,
                validation: {
                    initials: true,
                    doctorFullName: true
                },
            });
        }
    }

    handleInputChange = (e: FormEvent<FormControl & HTMLInputElement & HTMLSelectElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;

        if (name == 'initials') {
            this.setState({
                initials: value
            });
        }

        if (name == 'doctorFullName') {
            this.setState({
                doctorFullName: value
            });
        }

        if (name == 'additionalDays') {
            let additionalDays = parseInt(e.currentTarget.value);
            let totalDaysRequested = this.props.data.totalWagesReimbursedDays + additionalDays;

            //handle weeks 3/4
            let showMedicalReasonConfirmation = true;
            let showDoctorConfirmation = false;

            if (totalDaysRequested > 20) {
                showMedicalReasonConfirmation = false;
                showDoctorConfirmation = true;
            }

            this.setState({
                additionalDays,
                showMedicalReasonConfirmation,
                showDoctorConfirmation,
            });
        }
    }

    handleRequestAdditionalDays = () => {
        let validation = {
            initials: this.state.initials != '',
            doctorFullName: this.state.showDoctorConfirmation ? this.state.doctorFullName != '' : true
        };

        this.setState({
            validation,
            dirty: true
        }, () => {
            if (this.state.validation.initials && this.state.validation.doctorFullName) {
                this.props.onRequestAdditionalDays(this.props.data.id, this.state.additionalDays, this.state.initials, this.state.doctorFullName);
            }
        });
    }

    render = () => {
        if (this.props.data == null)
            return null;

        return (
            <Modal backdrop='static' show={this.props.show} onHide={this.props.onCancel}>
                <Modal.Header closeButton>
                    <Modal.Title>Request Additional Wage Reimbursement for <strong>{this.props.data.firstName + ' ' + this.props.data.lastName}</strong></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert bsStyle='info'>
                        We default to reimbursing two weeks of lost wages. If the donor has a medical need to miss more than 2 weeks of work, you may indicate the number
                        of additional work days that will be missed (up to 10 work days).
                    </Alert>

                    <FormGroup>
                        <ControlLabel>Additional days requested?</ControlLabel>
                        <FormControl
                            componentClass='select'
                            name='additionalDays'
                            value={this.state.additionalDays}
                            onChange={this.handleInputChange}
                        >
                            {this.state.optionsList.map(day =>
                                <option value={day}>{day} work day{day > 1 ? 's' : ''} </option>
                            )}
                        </FormControl>
                    </FormGroup>

                    {!this.state.showDoctorConfirmation ?
                        <FormGroup validationState={this.state.dirty && this.state.validation.initials == false ? 'error' : null}>
                            <ControlLabel>
                                Please confirm there is a medical reason the donor cannot work in their field/career.
                            </ControlLabel>
                            <FormControl
                                className='input-group'
                                id='initials'
                                name='initials'
                                placeholder='Your Initials'
                                maxLength={3}
                                value={this.state.initials}
                                onChange={this.handleInputChange}
                            />
                        </FormGroup>
                        :
                        <FormGroup validationState={this.state.dirty && (this.state.validation.initials == false || this.state.validation.doctorFullName == false) ? 'error' : null}>
                            <ControlLabel>
                                A doctor has determined that it would not be safe for the donor to return to work in their field/career and needs an additional 2 weeks for recovery.
                            </ControlLabel>

                            <Row style={{ marginTop: '10px' }}>
                                <div className='col-xs-12 col-sm-6'>
                                    <FormControl
                                        className='input-group'
                                        id='initials'
                                        name='initials'
                                        placeholder='Your Initials'
                                        maxLength={3}
                                        value={this.state.initials}
                                        onChange={this.handleInputChange}
                                    />
                                </div>
                                <div className='col-xs-12 col-sm-6'>
                                    <FormControl
                                        className='input-group'
                                        type='text'
                                        id='doctorFullName'
                                        name='doctorFullName'
                                        placeholder={`Requesting Doctor's Name`}
                                        maxLength={32}
                                        value={this.state.doctorFullName}
                                        onChange={this.handleInputChange}
                                    />
                                </div>
                            </Row>
                        </FormGroup>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle='primary' onClick={this.handleRequestAdditionalDays}>Complete Request</Button>
                    <Button onClick={this.props.onCancel}>Cancel</Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default RequestAdditional;